import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MachineDetails.css';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { redirect } from '../../common/utils/redirectionutils';
import { PAGE_CONST, TIMELINE_CONST, ACTIVE_DEVICE } from '../../common/constant/common-constants';
import { getMachineDetails } from '../../helper/MachineDetailsHelper';
import { connect } from 'react-redux';
import {
  selectMachineToUpdateVersionButton,
  setActiveSystemUpdateIcon,
  setSystemUpdateFullScreenModal,
  showUpdateMachineVersionNotification,
} from '../../modules/actions/action';
import MessageNotification from '../../components/MessageNotification/MessageNotification';
import PageStickyChrome from '../../components/PageStickyChrome/PageStickyChrome';
import PageFilterSearch from '../../components/PageStickyChrome/PageFilterSearch';
import { PageStatIcon } from '../../components/PageStickyChrome/PageStatIcons';
import { getSystemVersionDetails } from '../../helper/SystemVersionDetailsHelper';
import { get_NX5_RegistrationDetails } from '../../helper/NX5/NX5_RegistrationDetailsHelper';
import { isUnregisteredNx5Machine } from '../../helper/NX5/enrollmentToken';
import { ROLE } from '../../enum/role';
import { formatMachineSerial } from '../../utils/machinePayloadUtils';
import UnregisteredMachinesTable from './UnregisteredMachinesTable';
import HospitalDropdown from '../../components/HospitalDropdown/HospitalDropdown';
import { getMachineDetailsSession, setMachineDetailsSession, clearMachineDetailsSession } from './machineDetailsSession';

const FLEET_FILTER = {
  ALL: 'all',
  SUCCESSFUL: 'successful',
  PENDING: 'pending',
};

const FLEET_ENV = {
  ON_FIELD: 'Prod',
  TEMPORARY: 'Test',
  UNREGISTERED: 'Unregistered',
};

const FleetEnvPills = ({ className, selectedEnv, onSelect, activeDevice }) => {
  const tabs = [
    { id: FLEET_ENV.ON_FIELD, label: 'On Field' },
    { id: FLEET_ENV.TEMPORARY, label: 'Temporary-Registered' },
  ];
  if (activeDevice === ACTIVE_DEVICE.NX5) {
    tabs.push({ id: FLEET_ENV.UNREGISTERED, label: 'Unregistered' });
  }

  return (
    <div className={className} role="tablist" aria-label="Machine registration">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={selectedEnv === tab.id}
          className={`fleet-env-pill${selectedEnv === tab.id ? ' fleet-env-pill--active' : ''}`}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const getDisplaySerial = (machineUDI, activeDevice) => (
  formatMachineSerial(machineUDI, activeDevice)
);

const normalizeStatus = (status) => {
  if (!status) return { label: 'Pending', kind: 'pending' };
  const normalized = String(status).toLowerCase();
  if (normalized === 'successful') return { label: 'Updated', kind: 'success' };
  if (normalized === 'pending') return { label: 'Pending', kind: 'pending' };
  if (normalized === 'rollback') return { label: 'Rollback', kind: 'rollback' };
  return { label: status, kind: 'pending' };
};

const isSuccessfulDevice = (item) => (
  String(item.systemUpdateStatus ?? '').toLowerCase() === 'successful'
);

const normalizeSerial = (value) => String(value || '').trim().toLowerCase();

const matchesHospitalSerial = (item, hospitalFilter) => {
  if (!hospitalFilter) return true;
  const hospitalSerials = new Set(
    (hospitalFilter.serials || []).map(normalizeSerial).filter(Boolean)
  );
  if (!hospitalSerials.size) return false;

  const udi = normalizeSerial(item.machineUDI);
  if (!udi) return false;
  if (hospitalSerials.has(udi)) return true;

  return udi.split('-').some((part) => part && hospitalSerials.has(part));
};

const matchesSearch = (item, query, activeDevice) => {
  if (!query) return true;
  const q = String(query).trim().toLowerCase();
  if (!q) return true;

  const serial = getDisplaySerial(item.machineUDI, activeDevice);
  return (
    String(item.machineUDI || '').toLowerCase().includes(q) ||
    String(serial || '').toLowerCase().includes(q)
  );
};

const StatusIcon = ({ kind }) => {
  if (kind === 'success') {
    return (
      <svg className="fleet-card-status-icon fleet-card-status-icon--success" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="8.5" fill="#ecfdf3" stroke="#21a366" strokeWidth="1.5" />
        <path d="M6.5 10.2 8.8 12.5 13.5 7.8" fill="none" stroke="#21a366" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (kind === 'rollback') {
    return (
      <svg className="fleet-card-status-icon fleet-card-status-icon--rollback" viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="8.5" fill="#e8fbfd" stroke="#1ecfdb" strokeWidth="1.5" />
        <path d="M7 10h6M10 7l3 3-3 3" fill="none" stroke="#1ecfdb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg className="fleet-card-status-icon fleet-card-status-icon--pending" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="8.5" fill="#fff7ed" stroke="#f59e0b" strokeWidth="1.5" />
      <path d="M10 6v4l2.5 2.5" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const FleetDeviceCard = ({ device, selected, onToggleSelect, onCardClick, activeDevice }) => {
  const status = normalizeStatus(device.systemUpdateStatus);
  const serial = getDisplaySerial(device.machineUDI, activeDevice);
  const timestamp = device.systemUpdateDate
    ? moment(device.systemUpdateDate).format(TIMELINE_CONST.DATE_TIME_FORMAT)
    : null;

  return (
    <article
      className={`fleet-device-card${selected ? ' fleet-device-card--selected' : ''}`}
      onClick={(event) => onCardClick(device, event)}
    >
      <div className="fleet-device-card-head">
        <label className="fleet-device-checkbox" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(device.machineUDI)}
          />
        </label>
        <span className="fleet-device-serial">{serial}</span>
        <StatusIcon kind={status.kind} />
      </div>

      <div className="fleet-device-version-table">
        <div className="fleet-device-version-head">
          <span />
          <span>CURRENT</span>
          <span>SENT</span>
        </div>
        <div className="fleet-device-version-row">
          <span className="fleet-device-version-label">SYSTEM</span>
          <span className="fleet-device-version-val">{displayValue(device.currentSoftwareVersion)}</span>
          <span className="fleet-device-version-val">{displayValue(device.sentSoftwareVersion)}</span>
        </div>
        <div className="fleet-device-version-row">
          <span className="fleet-device-version-label">OS</span>
          <span className="fleet-device-version-val">{displayValue(device.currentOSVersion)}</span>
          <span className="fleet-device-version-val">{displayValue(device.sentOsVersion)}</span>
        </div>
      </div>

      <div className={`fleet-device-card-foot fleet-device-card-foot--${status.kind}`}>
        <span className="fleet-device-foot-status">{status.label}</span>
        {timestamp && <span className="fleet-device-foot-time">{timestamp}</span>}
      </div>
    </article>
  );
};

const MachineVersionDetails = (props) => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const savedSession = getMachineDetailsSession();
  const [searchQuery, setSearchQuery] = useState(savedSession.searchQuery);
  const [dataContainer, setDataContainer] = useState([]);
  const [versionContainer, setVersionContainer] = useState({});
  const [selectedRows, setSelectedRows] = useState(() => [...savedSession.selectedRows]);
  const [selectedEnv, setSelectedEnv] = useState(savedSession.selectedEnv);
  const [statusFilter, setStatusFilter] = useState(savedSession.statusFilter);
  const [hospitalFilter, setHospitalFilter] = useState(savedSession.hospitalFilter || null);
  const prevSearchQueryRef = useRef(savedSession.searchQuery);
  const prevSelectedEnvRef = useRef(savedSession.selectedEnv);
  const prevHospitalRef = useRef(savedSession.hospitalFilter?.CustomerNo || '');
  const prevActiveDeviceRef = useRef(props.activeDevice);

  useEffect(() => {
    props.selectMachineToUpdateVersion(selectedRows);
  }, [selectedRows, props]);

  useEffect(() => {
    setMachineDetailsSession({
      searchQuery,
      selectedRows,
      selectedEnv,
      statusFilter,
      hospitalFilter,
    });
  }, [searchQuery, selectedRows, selectedEnv, statusFilter, hospitalFilter]);

  useEffect(() => {
    handleMachineDataGet();
  }, [props.activeDevice]);

  useEffect(() => {
    if (!props.showUpdateNotification) return;

    handleMachineDataGet();
    setSelectedRows([]);
  }, [props.showUpdateNotification]);

  useEffect(() => {
    handleSystemVersionDataGet();
  }, [props.activeDevice]);

  useEffect(() => {
    if (selectedRows.length) {
      props.activateSystemUpdateButton();
    } else {
      props.deactivateSystemUpdateButton();
    }
  }, [selectedRows, props]);

  useEffect(() => {
    if (prevActiveDeviceRef.current === props.activeDevice) return;

    prevActiveDeviceRef.current = props.activeDevice;
    clearMachineDetailsSession();
    setSearchQuery('');
    setSelectedRows([]);
    setSelectedEnv(FLEET_ENV.ON_FIELD);
    setStatusFilter(FLEET_FILTER.ALL);
    setHospitalFilter(null);
  }, [props.activeDevice]);

  useEffect(() => {
    if (props.activeDevice !== ACTIVE_DEVICE.NX5 && selectedEnv === FLEET_ENV.UNREGISTERED) {
      setSelectedEnv(FLEET_ENV.ON_FIELD);
    }
  }, [props.activeDevice, selectedEnv]);

  useEffect(() => {
    const searchChanged = prevSearchQueryRef.current !== searchQuery;
    const envChanged = prevSelectedEnvRef.current !== selectedEnv;
    const hospitalChanged = prevHospitalRef.current !== (hospitalFilter?.CustomerNo || '');

    prevSearchQueryRef.current = searchQuery;
    prevSelectedEnvRef.current = selectedEnv;
    prevHospitalRef.current = hospitalFilter?.CustomerNo || '';

    if (searchChanged || envChanged || hospitalChanged) {
      setStatusFilter(FLEET_FILTER.ALL);
    }
  }, [searchQuery, selectedEnv, hospitalFilter]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleMachineDataGet = () => {
    if (props.activeDevice === ACTIVE_DEVICE.V730i) {
      getMachineDetails()
        .then((res) => setDataContainer(res.data.data || []))
        .catch((err) => console.log(err));
      return;
    }

    if (props.activeDevice === ACTIVE_DEVICE.NX5) {
      get_NX5_RegistrationDetails()
        .then((res) => setDataContainer(res.data || []))
        .catch((err) => console.log(err));
    }
  };

  const handleSystemVersionDataGet = () => {
    getSystemVersionDetails()
      .then((res) => setVersionContainer(res.data || {}))
      .catch((err) => console.log(err));
  };

  const baseFilteredDevices = useMemo(() => {
    if (!Array.isArray(dataContainer)) return [];

    return dataContainer.filter((item) => {
      if (!matchesSearch(item, searchQuery, props.activeDevice)) return false;

      if (props.activeDevice === ACTIVE_DEVICE.NX5 && selectedEnv === FLEET_ENV.UNREGISTERED) {
        return isUnregisteredNx5Machine(item);
      }

      if (!matchesHospitalSerial(item, hospitalFilter)) return false;
      if (props.activeDevice === ACTIVE_DEVICE.V730i) {
        return selectedEnv === FLEET_ENV.ON_FIELD ? item.isPermanent === true : item.isPermanent === false;
      }

      if (props.activeDevice === ACTIVE_DEVICE.NX5) {
        if (selectedEnv === FLEET_ENV.ON_FIELD) {
          return item.isTemporary === true && item.isPermanent === true;
        }
        return item.isTemporary === true && item.isPermanent === false;
      }

      return true;
    });
  }, [dataContainer, searchQuery, selectedEnv, hospitalFilter, props.activeDevice]);

  const isUnregisteredView = props.activeDevice === ACTIVE_DEVICE.NX5
    && selectedEnv === FLEET_ENV.UNREGISTERED;
  const canIssueEnrollment = props.userRole === ROLE.ADMIN || props.userRole === ROLE.PRODUCTION;

  useEffect(() => {
    if (!isUnregisteredView || selectedRows.length === 0) return;
    setSelectedRows([]);
  }, [isUnregisteredView, selectedRows.length]);

  const filteredDevices = useMemo(() => {
    if (isUnregisteredView) return baseFilteredDevices;
    if (statusFilter === FLEET_FILTER.SUCCESSFUL) {
      return baseFilteredDevices.filter(isSuccessfulDevice);
    }
    if (statusFilter === FLEET_FILTER.PENDING) {
      return baseFilteredDevices.filter((item) => !isSuccessfulDevice(item));
    }
    return baseFilteredDevices;
  }, [baseFilteredDevices, statusFilter, isUnregisteredView]);

  const stats = useMemo(() => {
    const total = baseFilteredDevices.length;
    const successful = baseFilteredDevices.filter(isSuccessfulDevice).length;
    const pending = total - successful;
    return { total, successful, pending };
  }, [baseFilteredDevices]);

  const isAllSelected = filteredDevices.length > 0
    && filteredDevices.every((item) => selectedRows.includes(item.machineUDI));

  const selectedMachines = useMemo(() => (
    selectedRows.map((machineUDI) => ({
      machineUDI,
      serial: getDisplaySerial(machineUDI, props.activeDevice),
    }))
  ), [selectedRows, props.activeDevice]);

  const latestSystemVersion = versionContainer?.prod?.[0]?.softwareVersion?.trim() || '—';
  const latestOsVersion = versionContainer?.prod?.[0]?.osVersion?.trim() || '—';

  const handleSelectAll = () => {
    if (isAllSelected) {
      const filteredIds = new Set(filteredDevices.map((item) => item.machineUDI));
      setSelectedRows((prev) => prev.filter((id) => !filteredIds.has(id)));
      return;
    }

    const merged = new Set([...selectedRows, ...filteredDevices.map((item) => item.machineUDI)]);
    setSelectedRows([...merged]);
  };

  const handleToggleSelect = useCallback((machineUDI) => {
    setSelectedRows((prev) => (
      prev.includes(machineUDI)
        ? prev.filter((id) => id !== machineUDI)
        : [...prev, machineUDI]
    ));
  }, []);

  const handleCardClick = (device, event) => {
    if (event.target?.type === 'checkbox') return;
    if (props.activeDevice === ACTIVE_DEVICE.V730i) {
      redirect(navigate, PAGE_CONST.MACHINE_DETAILS, device);
    }
  };

  const machinePageLabel = props.activeDevice === ACTIVE_DEVICE.NX5
    ? `${ACTIVE_DEVICE.NX5} Machine`
    : `${ACTIVE_DEVICE.V730i} Machine`;

  return (
    <div className="fleet-page">
      {props.showUpdateNotification && (
        <div className="fleet-notification-wrap">
          <MessageNotification variant="machineUpdate" />
        </div>
      )}

      <PageStickyChrome>
      <div className="page-sticky-chrome__details">
        <div className="fleet-breadcrumb">Admin Dashboard &gt; {machinePageLabel}</div>
        <FleetEnvPills
          className="fleet-env-pills fleet-env-pills--details"
          selectedEnv={selectedEnv}
          onSelect={setSelectedEnv}
          activeDevice={props.activeDevice}
        />
        <div className="fleet-latest-versions">
          <span>Latest System Version: <strong>{latestSystemVersion}</strong></span>
          <span>Latest OS Version: <strong>{latestOsVersion}</strong></span>
        </div>
      </div>

      <div className="fleet-header">
        <div className="fleet-header-left">
          <div className="fleet-title-row">
            <h3 className="fleet-title">{machinePageLabel}</h3>
            <FleetEnvPills
              className="fleet-env-pills fleet-env-pills--inline"
              selectedEnv={selectedEnv}
              onSelect={setSelectedEnv}
              activeDevice={props.activeDevice}
            />
          </div>
        </div>
        
        {!isUnregisteredView && (
          <HospitalDropdown
            className="fleet-hospital-dropdown"
            value={hospitalFilter?.CustomerNo || ''}
            onChange={(hospital) => {
              if (!hospital) {
                setHospitalFilter(null);
                return;
              }
              setHospitalFilter({
                CustomerNo: hospital.CustomerNo,
                Name: hospital.Name,
                serials: (hospital.machines || [])
                  .map((machine) => machine?.SerialNo)
                  .filter(Boolean),
              });
            }}
          />
        )}

        <div className="fleet-header-actions">
          <PageFilterSearch inputRef={searchInputRef}>
            <input
              ref={searchInputRef}
              type="search"
              className="fleet-search-input"
              placeholder="Search machine number…"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              aria-label="Search machines"
            />
          </PageFilterSearch>
          {!isUnregisteredView && (props.activeDevice === ACTIVE_DEVICE.V730i || props.activeDevice === ACTIVE_DEVICE.NX5) && (
            <button
              type="button"
              className={`fleet-update-btn${props.updateSystemIcon ? '' : ' fleet-update-btn--disabled'}`}
              disabled={!props.updateSystemIcon}
              onClick={() => props.openModal()}
            >
              <span className="fleet-update-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M12 5v10M8.5 11.5 12 15l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><path d="M5 19h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              </span>
              Update
            </button>
          )}
        </div>
      </div>

      {!isUnregisteredView && (
      <div className="fleet-stats-grid">
        <button
          type="button"
          className={`fleet-stat-card fleet-stat-card--total${statusFilter === FLEET_FILTER.ALL ? ' fleet-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(FLEET_FILTER.ALL)}
          aria-pressed={statusFilter === FLEET_FILTER.ALL}
        >
          <PageStatIcon name="total" />
          <span className="fleet-stat-label">TOTAL MACHINES</span>
          <span className="fleet-stat-value">{stats.total}</span>
        </button>
        <button
          type="button"
          className={`fleet-stat-card fleet-stat-card--success${statusFilter === FLEET_FILTER.SUCCESSFUL ? ' fleet-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(FLEET_FILTER.SUCCESSFUL)}
          aria-pressed={statusFilter === FLEET_FILTER.SUCCESSFUL}
        >
          <PageStatIcon name="success" />
          <span className="fleet-stat-label">SUCCESSFUL</span>
          <span className="fleet-stat-value">{stats.successful}</span>
        </button>
        <button
          type="button"
          className={`fleet-stat-card fleet-stat-card--pending${statusFilter === FLEET_FILTER.PENDING ? ' fleet-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(FLEET_FILTER.PENDING)}
          aria-pressed={statusFilter === FLEET_FILTER.PENDING}
        >
          <PageStatIcon name="pending" />
          <span className="fleet-stat-label">PENDING</span>
          <span className="fleet-stat-value">{stats.pending}</span>
        </button>
      </div>
      )}

      {!isUnregisteredView && (
      <div className="page-sticky-chrome__toolbar fleet-toolbar">
        <label className="fleet-select-all">
          <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
          <span>Select All ({filteredDevices.length})</span>
        </label>
        {selectedMachines.length > 0 && (
          <div className="fleet-selected" aria-live="polite">
            <span className="fleet-selected-label">
              Selected ({selectedMachines.length})
            </span>
            <div className="fleet-selected-chips">
              {selectedMachines.map((machine) => (
                <button
                  key={machine.machineUDI}
                  type="button"
                  className="fleet-selected-chip"
                  title={machine.machineUDI}
                  onClick={() => handleToggleSelect(machine.machineUDI)}
                >
                  <span>{machine.serial}</span>
                  <span className="fleet-selected-chip-remove" aria-hidden="true">×</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="fleet-selected-clear"
              onClick={() => setSelectedRows([])}
            >
              Clear
            </button>
          </div>
        )}
      </div>
      )}
      </PageStickyChrome>

      {isUnregisteredView ? (
        <UnregisteredMachinesTable
          devices={filteredDevices}
          canIssue={canIssueEnrollment}
        />
      ) : (
      <div className="fleet-cards-scroll">
        {filteredDevices.length === 0 ? (
          <div className="fleet-empty">No machines found.</div>
        ) : (
          <div className="fleet-cards-grid">
            {filteredDevices.map((device) => (
              <FleetDeviceCard
                key={device.machineUDI}
                device={device}
                selected={selectedRows.includes(device.machineUDI)}
                onToggleSelect={handleToggleSelect}
                onCardClick={handleCardClick}
                activeDevice={props.activeDevice}
              />
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  updateSystemIcon: state.updateSystemIcon.activeUpdateSystemButton,
  showUpdateNotification: state.showMachineUpdateNotification.showMachineVersionNotification,
  activeDevice: state?.setactiveDevice?.device,
  userRole: state.userDetails.userRole,
});

const mapDispatchToProps = (dispatch) => ({
  activateSystemUpdateButton: () => dispatch(setActiveSystemUpdateIcon(true)),
  deactivateSystemUpdateButton: () => dispatch(setActiveSystemUpdateIcon(false)),
  selectMachineToUpdateVersion: (machine) => dispatch(selectMachineToUpdateVersionButton(machine)),
  showMachineUpdateNotification: () => dispatch(showUpdateMachineVersionNotification(true)),
  closeMachineUpdateNotification: () => dispatch(showUpdateMachineVersionNotification(false)),
  openModal: () => dispatch(setSystemUpdateFullScreenModal(true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MachineVersionDetails);
